/**
 * Utilitaires fractions partagés par tous les générateurs 6ème (gen_620 à gen_636).
 *
 * Deux styles d'appel supportés :
 * - Style objet (gen_620-625) : mkFrac(3,4) → {num:3, den:4}, renderFrac(frac) → "\dfrac{3}{4}"
 * - Style 2 args (gen_626-636) : fmtFrac(3,4) → "3/4", fmtKatex(3,4) → "\dfrac{3}{4}"
 */

// ─────────────────────────────────────────────────
// Arithmétique de base
// ─────────────────────────────────────────────────

/** PGCD (Euclide). */
export const gcd = (a, b) => {
    a = Math.abs(a);
    b = Math.abs(b);
    while (b) { [a, b] = [b, a % b]; }
    return a || 1;
};

/** Crée une fraction simplifiée { num, den } avec den > 0. */
export const mkFrac = (num, den) => {
    if (den === 0) throw new Error('mkFrac: dénominateur nul');
    if (num === 0) return { num: 0, den: 1 };
    if (den < 0) { num = -num; den = -den; }
    const g = gcd(Math.abs(num), den);
    return { num: num / g, den: den / g };
};

/** Simplifie — alias de mkFrac pour compatibilité gen_626-636. */
export const simplify = (a, b) => mkFrac(a, b);

// ─────────────────────────────────────────────────
// Détection décimale (gen_620-625)
// ─────────────────────────────────────────────────

/** Teste si la fraction a une écriture décimale finie (dén = 2^a × 5^b). */
export const isDecimal = (frac) => {
    let d = (typeof frac === 'object') ? frac.den : frac;
    while (d % 2 === 0) d /= 2;
    while (d % 5 === 0) d /= 5;
    return d === 1;
};

/** Convertit en string décimal français (virgule). Retourne null si non décimal. */
export const toDecimalString = (frac) => {
    if (!isDecimal(frac)) return null;
    const val = frac.num / frac.den;
    if (Number.isInteger(val)) return String(val);
    const str = val.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
    return str.replace('.', ',');
};

// ─────────────────────────────────────────────────
// Formatage — style objet (gen_620-625)
// ─────────────────────────────────────────────────

/** Formate un objet fraction en KaTeX \dfrac{num}{den}. Si den=1, affiche l'entier. */
export const renderFrac = (frac) => {
    const { num, den } = (typeof frac === 'object') ? frac : mkFrac(frac, 1);
    if (den === 1) return String(num);
    return `\\dfrac{${num}}{${den}}`;
};

// ─────────────────────────────────────────────────
// Formatage — style 2 args (gen_626-636)
// ─────────────────────────────────────────────────

/** Texte plat simplifié "3/4" — pour les réponses saisie (l'élève tape du texte). */
export const fmtFrac = (a, b) => {
    const s = mkFrac(a, b);
    return s.den === 1 ? String(s.num) : `${s.num}/${s.den}`;
};

/** KaTeX brut "\dfrac{3}{4}" — pour les énoncés via expression elements. */
export const fmtKatex = (a, b) => renderFrac(mkFrac(a, b));

/** KaTeX avec délimiteurs "$\dfrac{3}{4}$" — pour les réponses interactives (V/F, QCM, Relier, Classer). */
export const fmtItem = (a, b) => {
    const s = mkFrac(a, b);
    return s.den === 1 ? String(s.num) : `$${renderFrac(s)}$`;
};

/**
 * Formate SANS SIMPLIFIER — pour les exercices où l'écriture étendue EST
 * l'objet d'étude (classer 2/6 dans la colonne 1/3, reconnaître que 3/3 = 1) :
 * fmtItem passe par mkFrac qui simplifie, et afficherait donc la réponse.
 */
export const fmtItemBrut = (num, den) =>
    (den === 1 ? String(num) : `$\\dfrac{${num}}{${den}}$`);
